<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">新增時間區間給點規則</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleCancel">
          <i class="ri-close-line"></i>
          取消
        </button>
        <button type="button" class="btn btn-primary" :disabled="submitting" @click="handleSubmit">
          <span v-if="submitting" class="spinner spinner-sm"></span>
          <span v-else>建立規則</span>
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="create-rule-container">

    <div class="form-container">
      <form @submit.prevent="handleSubmit">
        <div class="form-section">
          <h3 class="section-title">基本資訊</h3>
          
          <div class="form-group">
            <label class="required">規則名稱</label>
            <input
              v-model="form.ruleName"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': errors.ruleName }"
              placeholder="例如：週年慶雙倍點數"
              required
              maxlength="200"
            />
            <div v-if="errors.ruleName" class="invalid-feedback">
              {{ errors.ruleName }}
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="required">開始時間</label>
              <input
                v-model="form.startTime"
                type="datetime-local"
                class="form-control"
                :class="{ 'is-invalid': errors.startTime }"
                required
              />
              <div v-if="errors.startTime" class="invalid-feedback">
                {{ errors.startTime }}
              </div>
            </div>

            <div class="form-group">
              <label class="required">結束時間</label>
              <input
                v-model="form.endTime"
                type="datetime-local"
                class="form-control"
                :class="{ 'is-invalid': errors.endTime }"
                required
              />
              <div v-if="errors.endTime" class="invalid-feedback">
                {{ errors.endTime }}
              </div>
            </div>
          </div>

          <div v-if="timeOverlapWarning" class="alert alert-warning">
            <i class="ri-alert-line"></i>
            {{ timeOverlapWarning }}
          </div>
        </div>

        <div class="form-section">
          <TimeRangeCalcSettingsPanel
            v-model:use-normal-calculation-settings="form.useNormalCalculationSettings"
            :custom-settings="form"
            :normal-rule="normalRule"
            :normal-rule-loading="normalRuleLoading"
            :errors="errors"
          />
        </div>

        <div class="form-section">
          <h3 class="section-title">會員等級回饋率設定</h3>
          <p class="section-description">設定活動期間各等級回饋率；留空則沿用常態規則的回饋率</p>

          <div v-if="tiers.length === 0" class="alert alert-warning">
            <p>尚未設定會員等級，請先前往 <router-link to="/client/member-tiers">會員等級管理</router-link> 建立等級</p>
          </div>

          <div v-else class="tier-config-list">
            <div v-for="tier in tiers" :key="tier.id" class="tier-config-item">
              <div class="tier-info">
                <span v-if="tier.color" class="tier-name" :style="{ color: tier.color }">
                  {{ tier.tierName }}
                </span>
                <span v-else class="tier-name">{{ tier.tierName }}</span>
                <code class="tier-code">{{ tier.tierCode }}</code>
              </div>
              <div class="tier-input">
                <div class="input-group">
                  <input
                    v-model.number="form.tierConfig[tier.tierCode]"
                    type="number"
                    class="form-control"
                    :class="{ 'is-invalid': errors[`tier_${tier.tierCode}`] }"
                    placeholder="留空使用常態規則"
                    min="0"
                    step="1"
                  />
                  <span class="input-group-text">%</span>
                </div>
                <div v-if="errors[`tier_${tier.tierCode}`]" class="invalid-feedback">
                  {{ errors[`tier_${tier.tierCode}`] }}
                </div>
                <small class="form-text">留空則使用常態規則的回饋率；可超過 100%，須為整數</small>
              </div>
            </div>
          </div>
        </div>

        <div class="form-section">
          <h3 class="section-title">狀態設定</h3>

          <div class="form-group">
            <label>初始狀態</label>
            <select v-model="form.status" class="form-control">
              <option value="draft">草稿（稍後手動啟用）</option>
              <option value="scheduled">已排程（開始時間到達時自動啟用）</option>
              <option value="active">立即啟用（如果時間區間已開始）</option>
            </select>
            <small class="form-text">選擇「已排程」或「立即啟用」時，系統會檢查時間區間是否與其他規則重疊</small>
          </div>
        </div>

        <div class="form-section">
          <h3 class="section-title">備註（選填）</h3>
          <div class="form-group">
            <label>備註說明</label>
            <textarea
              v-model="form.remark"
              class="form-control"
              :class="{ 'is-invalid': errors.remark }"
              rows="3"
              placeholder="請輸入此規則的備註說明（選填）"
              maxlength="500"
            ></textarea>
            <div v-if="errors.remark" class="invalid-feedback">
              {{ errors.remark }}
            </div>
            <small class="form-text">此備註將記錄在變更歷史中，方便日後追蹤規則建立原因（最多 500 字）</small>
          </div>
        </div>

      </form>
    </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, reactive, watch, computed } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { pointsEarningRulesService } from '@/api/modules/pointsEarningRules.js';
import { memberTierService } from '@/api/modules/memberTiers.js';
import { showSuccess, showError } from '@/utils/swal.js';
import { fromDatetimeLocal } from '@/utils/datetimeLocal.js';
import TimeRangeCalcSettingsPanel from './TimeRangeCalcSettingsPanel.vue';

export default {
  name: 'PointsEarningRulesTimeRangeCreate',
  components: {
    TimeRangeCalcSettingsPanel
  },
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('新增時間區間給點規則');
    const submitting = ref(false);
    const tiers = ref([]);
    const normalRule = ref(null);
    const normalRuleLoading = ref(false);
    const timeOverlapWarning = ref('');

    const form = reactive({
      ruleName: '',
      startTime: '',
      endTime: '',
      tierConfig: {},
      useNormalCalculationSettings: true,
      calculationBase: 'paid_amount',
      roundingMethod: 'floor',
      baseAmount: 1,
      effectiveDays: 0,
      status: 'draft',
      remark: ''
    });

    const errors = reactive({});

    const fetchNormalRule = async () => {
      normalRuleLoading.value = true;
      try {
        const response = await pointsEarningRulesService.getNormalRule();
        if (response && response.success && response.data) {
          normalRule.value = response.data;
        }
      } catch (err) {
        console.error('獲取常態規則失敗:', err);
      } finally {
        normalRuleLoading.value = false;
      }
    };

    const fetchTiers = async () => {
      try {
        const response = await memberTierService.getTiers({ isActive: true });
        if (response && response.success && response.data) {
          tiers.value = response.data;
          // 初始化 tierConfig
          tiers.value.forEach(tier => {
            form.tierConfig[tier.tierCode] = null; // 預設為 null，表示使用常態規則
          });
        }
      } catch (err) {
        console.error('獲取會員等級列表失敗:', err);
      }
    };

    // 檢查時間區間重疊（當時間變更時）
    watch([() => form.startTime, () => form.endTime], () => {
      if (form.startTime && form.endTime && (form.status === 'active' || form.status === 'scheduled')) {
        // 這裡可以添加即時檢查邏輯（可選）
        timeOverlapWarning.value = '';
      }
    });

    const handleSubmit = async () => {
      // 清除錯誤
      Object.keys(errors).forEach(key => delete errors[key]);
      timeOverlapWarning.value = '';

      // 驗證
      let hasError = false;

      if (!form.ruleName || form.ruleName.trim().length === 0) {
        errors.ruleName = '規則名稱不能為空';
        hasError = true;
      }

      if (!form.startTime) {
        errors.startTime = '開始時間不能為空';
        hasError = true;
      }

      if (!form.endTime) {
        errors.endTime = '結束時間不能為空';
        hasError = true;
      }

      if (form.startTime && form.endTime) {
        const start = new Date(form.startTime);
        const end = new Date(form.endTime);
        if (start >= end) {
          errors.endTime = '結束時間必須晚於開始時間';
          hasError = true;
        }
      }

      for (const [tierCode, rate] of Object.entries(form.tierConfig)) {
        if (rate === null || rate === undefined || rate === '') {
          continue;
        }
        if (!Number.isInteger(rate) || rate < 0) {
          errors[`tier_${tierCode}`] = '回饋率須為非負整數';
          hasError = true;
        }
      }

      if (!form.useNormalCalculationSettings) {
        if (!Number.isInteger(form.baseAmount) || form.baseAmount < 1) {
          errors.baseAmount = '金額計算基數必須為正整數';
          hasError = true;
        }
        if (!Number.isInteger(form.effectiveDays) || form.effectiveDays < 0) {
          errors.effectiveDays = '生效天數不可為負數';
          hasError = true;
        }
      }

      if (hasError) {
        return;
      }

      submitting.value = true;

      try {
        // 過濾掉 null 值的 tierConfig
        const tierConfig = {};
        for (const [tierCode, rate] of Object.entries(form.tierConfig)) {
          if (rate !== null && rate !== undefined) {
            tierConfig[tierCode] = rate;
          }
        }

        const startTimeISO = fromDatetimeLocal(form.startTime);
        const endTimeISO = fromDatetimeLocal(form.endTime);

        const payload = {
          ruleName: form.ruleName.trim(),
          startTime: startTimeISO,
          endTime: endTimeISO,
          tierConfig,
          useNormalCalculationSettings: form.useNormalCalculationSettings,
          status: form.status,
          remark: form.remark || undefined
        };

        if (!form.useNormalCalculationSettings) {
          payload.calculationBase = form.calculationBase;
          payload.roundingMethod = form.roundingMethod;
          payload.baseAmount = form.baseAmount;
          payload.effectiveDays = form.effectiveDays;
        }

        const response = await pointsEarningRulesService.createTimeRangeRule(payload);

        if (response && response.success) {
          await showSuccess('建立成功', '時間區間給點規則已建立');
          router.push('/client/points/earning-rules/time-range');
        } else {
          throw new Error(response?.message || '建立失敗');
        }
      } catch (err) {
        console.error('建立規則失敗:', err);
        console.error('錯誤詳情:', {
          response: err.response,
          data: err.data,
          message: err.message,
          code: err.code,
          details: err.details
        });
        
        // 從多個可能的位置取得錯誤訊息
        const errorData = err.data || err.response?.data || {};
        const errorMessage = errorData.message || err.message || '建立規則時發生錯誤';
        
        // 檢查是否為時間重疊錯誤
        // 錯誤結構：{ success: false, error: { code: 'RULE_VALIDATION_001', details: { overlaps: [...] } }, message: '...' }
        const errorObj = errorData.error || {};
        if (errorObj.code === 'RULE_VALIDATION_001' && errorObj.details?.overlaps) {
          const overlaps = errorObj.details.overlaps;
          const overlapNames = overlaps.map(o => o.ruleName || o.rule_name || '未知規則').join('、');
          timeOverlapWarning.value = `時間區間與以下規則重疊：${overlapNames}`;
          await showError('建立失敗', `時間區間與其他啟用中的規則重疊：${overlapNames}`);
        } else {
          await showError('建立失敗', errorMessage);
        }
      } finally {
        submitting.value = false;
      }
    };

    const handleCancel = () => {
      router.push('/client/points/earning-rules/time-range');
    };

    onMounted(async () => {
      await Promise.all([fetchTiers(), fetchNormalRule()]);
    });

    return {
      pageTitle,
      submitting,
      tiers,
      normalRule,
      normalRuleLoading,
      form,
      errors,
      timeOverlapWarning,
      handleSubmit,
      handleCancel
    };
  }
};
</script>

<style scoped>
.create-rule-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.form-container {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.form-section {
  margin-bottom: 32px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #333;
}

.section-description {
  color: #666;
  margin-bottom: 20px;
  font-size: 14px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.tier-config-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.tier-config-item {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #f9f9f9;
}

.tier-info {
  flex: 0 0 200px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tier-name {
  font-weight: 600;
  font-size: 16px;
}

.tier-code {
  font-size: 12px;
  color: #666;
  background: #e0e0e0;
  padding: 2px 6px;
  border-radius: 4px;
}

.tier-input {
  flex: 1;
}

.input-group {
  max-width: 200px;
}

.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
}

.required::after {
  content: ' *';
  color: #dc3545;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }

  .tier-config-item {
    flex-direction: column;
  }

  .tier-info {
    flex: 1;
  }
}
</style>
